import React, { useEffect } from 'react'
import { Input as InputComponent } from '@/components/ui/input'
import { Checkbox as CheckboxComponent } from '@/components/ui/checkbox'
import { Label as LabelComponent } from '@/components/ui/label'
import {
    RadioGroup as RadioGroupComponent,
    RadioGroupItem as RadioGroupItemComponent,
} from '@/components/ui/radio-group'
import {
    Select as SelectComponent,
    SelectContent as SelectContentComponent,
    SelectItem as SelectItemComponent,
    SelectTrigger as SelectTriggerComponent,
    SelectValue as SelectValueComponent,
} from '@/components/ui/select'
import { useWarmupRecommendation } from '@/hooks/useWarmupRecommendation'
import {
    MIN_WARMUP_BUDGET,
    MODES,
    MODE_LABELS,
    START_CHOICES,
    START_CHOICE_HINTS,
    START_CHOICE_LABELS,
    defaultTargetEvent,
    describeWarmupProblem,
    eventLabel,
    targetEventOptions,
    warmupEventOptions,
} from '@/utils/pixel-warmup'
import { formatCurrencyAutoLocale } from '@/utils/tools'
import { AUTOMATED_NOT_ALLOWED_NOTE } from '@/utils/pixel-warmup-panel'

const Input = InputComponent as React.FC<React.InputHTMLAttributes<HTMLInputElement>>
const Label = LabelComponent as React.ComponentType<any>
const Checkbox = CheckboxComponent as React.ComponentType<any>
const RadioGroup = RadioGroupComponent as React.ComponentType<any>
const RadioGroupItem = RadioGroupItemComponent as React.ComponentType<any>
const Select = SelectComponent as React.ComponentType<any>
const SelectContent = SelectContentComponent as React.ComponentType<any>
const SelectItem = SelectItemComponent as React.ComponentType<any>
const SelectTrigger = SelectTriggerComponent as React.ComponentType<any>
const SelectValue = SelectValueComponent as React.ComponentType<any>

export interface WarmupChoice {
    startChoice?: string
    mode?: string
    targetEvent?: string
    warmupEvent?: string
    warmupBudget?: string
    maxDailyBudget?: string
    /** The user picked the mode in the mode select (not set by the Automatic start). */
    modePicked?: boolean
    /** The owner ticked the box that keeps the campaign out of the holdout comparison. */
    holdoutOptOut?: boolean
}

interface WarmupSectionProps {
    choice: WarmupChoice
    onChange: (next: WarmupChoice) => void
    objective: string
    /** Audience daily budgets as typed (major units). */
    audienceAmounts: number[]
    currency?: string
    /** Minimum daily budget of an ad set of the ad account (minor units). */
    minDailyBudget: number
    /** Called with whether the warm-up features are offered now (false while loading, when off or when the read failed). */
    onAvailabilityChange?: (offered: boolean) => void
}

const UNAVAILABLE_NOTES: Record<string, string> = {
    halted: 'New warm-ups are paused right now. Your campaign starts as usual.',
    currency_not_supported:
        'A warm-up and a maximum daily budget are not available yet for the currency of your ad account.',
}

const optionsOf = (recommendation: any, objective: string, targetEvent: string): string[] => {
    const candidates: string[] = (recommendation?.warmup_candidates || []).map((item: { event: string }) => item.event)
    return candidates.length > 0 ? candidates : warmupEventOptions(objective, targetEvent)
}

/**
 * The optional start of a new campaign: with or without a warm-up ad set, how it is run, and the maximum daily budget.
 * Nothing here is chosen for the user: the start choice is "No warm-up" until they pick another one, and the
 * recommendation is only shown. Renders nothing where the licence does not have the feature.
 */
const WarmupSection: React.FC<WarmupSectionProps> = ({
    choice,
    onChange,
    objective,
    audienceAmounts,
    currency,
    minDailyBudget,
    onAvailabilityChange,
}) => {
    const startChoice = choice.startChoice || START_CHOICES.DIRECT
    const targetEvent = choice.targetEvent || defaultTargetEvent(objective) || ''
    const { recommendation, featureAvailable } = useWarmupRecommendation({
        objective,
        targetEvent,
        audienceAdSets: audienceAmounts.length || undefined,
    })

    const offered = featureAvailable && !!recommendation
    useEffect(() => {
        onAvailabilityChange?.(offered)
    }, [offered, onAvailabilityChange])

    if (!offered) return null

    const unavailable = recommendation.unavailable_reason
    // Without a pixel the server refuses a warm-up at launch, so none is offered
    const canStartWarmup = recommendation.available === true && recommendation.reason !== 'no_pixel'
    const warmupOptions = optionsOf(recommendation, objective, targetEvent)
    const targetOptions = targetEventOptions(objective)
    const problem = describeWarmupProblem(
        { ...choice, startChoice, targetEvent },
        { audienceAmounts, minDailyBudget, objective },
    )
    const showsWarmupFields = startChoice !== START_CHOICES.DIRECT
    // The holdout notice and box: only for the automatic start, and only when the server says the holdout is on with a
    // valid share (a missing or false answer shows nothing)
    const showsHoldout =
        startChoice === START_CHOICES.AUTOMATIC &&
        recommendation.holdout_enabled === true &&
        Number.isInteger(recommendation.holdout_share)

    const pickStart = (value: string) => {
        // The exclusion box belongs to the automatic start only: another choice clears it
        if (value === START_CHOICES.DIRECT) {
            onChange({ ...choice, startChoice: value, holdoutOptOut: false })
            return
        }
        const options = optionsOf(recommendation, objective, targetEvent)
        const recommended = recommendation.warmup_event
        onChange({
            ...choice,
            startChoice: value,
            holdoutOptOut: value === START_CHOICES.AUTOMATIC ? choice.holdoutOptOut === true : false,
            targetEvent,
            // Automatic starts automated; leaving it returns to manual unless the user picked the mode themselves
            mode:
                value === START_CHOICES.AUTOMATIC
                    ? MODES.AUTOMATED
                    : choice.modePicked
                      ? choice.mode || MODES.MANUAL
                      : MODES.MANUAL,
            // The recommended event is offered only now that the user chose a warm-up
            warmupEvent:
                value === START_CHOICES.WARMUP
                    ? choice.warmupEvent || (options.includes(recommended) ? recommended : '')
                    : choice.warmupEvent,
            warmupBudget: choice.warmupBudget || String(Math.max(MIN_WARMUP_BUDGET, Number(minDailyBudget) || 0) / 100),
        })
    }

    // Automated mode, and so the automatic start, is offered only where the licence has it
    const automatedAllowed = recommendation.automated_allowed === true
    const startOptions = [
        START_CHOICES.DIRECT,
        ...(canStartWarmup ? [START_CHOICES.WARMUP, ...(automatedAllowed ? [START_CHOICES.AUTOMATIC] : [])] : []),
    ]

    return (
        <section className="tw:flex tw:flex-col tw:gap-4 tw:px-6 tw:pb-6" data-testid="warmup-section">
            <div>
                <h3 className="tw:text-lg tw:font-semibold tw:mb-1">Start of the campaign (optional)</h3>
                <p className="tw:text-sm tw:text-gray-200">
                    A warm-up collects pixel events with one broad ad set before your audience ad sets start. Leave it
                    off to launch everything now, as usual.
                </p>
            </div>

            {recommendation.message && (canStartWarmup || recommendation.reason === 'no_pixel') && (
                <div className="tw:text-sm tw:text-gray-200" data-testid="warmup-recommendation" role="status">
                    {canStartWarmup && (
                        <span className="tw:font-medium">
                            Recommendation:{' '}
                            {recommendation.start_mode === 'warmup' ? 'start with a warm-up' : 'no warm-up needed'}
                            .{' '}
                        </span>
                    )}
                    {recommendation.message}
                    {recommendation.target_event_count !== null && recommendation.target_event_count !== undefined && (
                        <span data-testid="warmup-recommendation-count">
                            {' '}
                            {recommendation.target_event_count} {eventLabel(recommendation.target_event)} events in the
                            last {recommendation.events_window_days} days
                            {recommendation.events_truncated ? ' or more' : ''} (a start without a warm-up is
                            recommended from {recommendation.seed_floor}).
                        </span>
                    )}
                </div>
            )}
            {unavailable && UNAVAILABLE_NOTES[unavailable] && (
                <div className="tw:text-sm tw:text-yellow-300" role="status" data-testid="warmup-unavailable">
                    {UNAVAILABLE_NOTES[unavailable]}
                </div>
            )}

            {canStartWarmup && !automatedAllowed && (
                <div className="tw:text-sm tw:text-gray-200" role="status" data-testid="warmup-automated-unavailable">
                    {AUTOMATED_NOT_ALLOWED_NOTE} A warm-up is confirmed by you before it expands.
                </div>
            )}

            <RadioGroup
                value={startChoice}
                onValueChange={pickStart}
                className="tw:flex tw:flex-col tw:gap-4"
                aria-label="Start of the campaign"
            >
                {startOptions.map((value) => (
                    <div key={value} className="tw:flex tw:items-start tw:gap-3">
                        <RadioGroupItem value={value} id={`warmup-start-${value}`} />
                        <div className="tw:flex tw:flex-col tw:gap-1">
                            <Label htmlFor={`warmup-start-${value}`} className="tw:cursor-pointer">
                                {(START_CHOICE_LABELS as Record<string, string>)[value]}
                            </Label>
                            <Label
                                htmlFor={`warmup-start-${value}`}
                                className="tw:text-sm tw:text-gray-200 tw:cursor-pointer"
                            >
                                {(START_CHOICE_HINTS as Record<string, string>)[value]}
                            </Label>
                        </div>
                    </div>
                ))}
            </RadioGroup>

            {showsWarmupFields && (
                <div className="tw:flex tw:flex-col tw:gap-4" data-testid="warmup-settings">
                    {targetOptions.length > 1 && (
                        <div className="tw:flex tw:flex-col tw:gap-1">
                            <Label htmlFor="warmup-target-event">Target event of the campaign</Label>
                            <Select
                                value={targetEvent}
                                onValueChange={(value: string) =>
                                    onChange({ ...choice, targetEvent: value, warmupEvent: '' })
                                }
                            >
                                <SelectTrigger id="warmup-target-event" className="tw:w-full">
                                    <SelectValue>{eventLabel(targetEvent)}</SelectValue>
                                </SelectTrigger>
                                <SelectContent className="tw:bg-primary tw:border-slate-700">
                                    {targetOptions.map((value: string) => (
                                        <SelectItem key={value} value={value} handleDelete={() => {}}>
                                            {eventLabel(value)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    {startChoice === START_CHOICES.WARMUP && (
                        <>
                            <div className="tw:flex tw:flex-col tw:gap-1">
                                <Label htmlFor="warmup-mode">How the expansion is run</Label>
                                <Select
                                    value={choice.mode || MODES.MANUAL}
                                    onValueChange={(value: string) =>
                                        onChange({ ...choice, mode: value, modePicked: true })
                                    }
                                >
                                    <SelectTrigger id="warmup-mode" className="tw:w-full">
                                        <SelectValue>
                                            {(MODE_LABELS as Record<string, string>)[choice.mode || MODES.MANUAL]}
                                        </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent className="tw:bg-primary tw:border-slate-700">
                                        {Object.values(MODES)
                                            .filter((value) => value !== MODES.AUTOMATED || automatedAllowed)
                                            .map((value) => (
                                                <SelectItem key={value} value={value} handleDelete={() => {}}>
                                                    {(MODE_LABELS as Record<string, string>)[value]}
                                                </SelectItem>
                                            ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="tw:flex tw:flex-col tw:gap-1">
                                <Label htmlFor="warmup-event">Event of the warm-up ad set</Label>
                                <Select
                                    value={choice.warmupEvent || ''}
                                    onValueChange={(value: string) => onChange({ ...choice, warmupEvent: value })}
                                >
                                    <SelectTrigger id="warmup-event" className="tw:w-full">
                                        <SelectValue placeholder="Choose an event">
                                            {choice.warmupEvent ? eventLabel(choice.warmupEvent) : undefined}
                                        </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent className="tw:bg-primary tw:border-slate-700">
                                        {warmupOptions.map((value) => (
                                            <SelectItem key={value} value={value} handleDelete={() => {}}>
                                                {eventLabel(value)}
                                                {recommendation.warmup_event === value ? ' (recommended)' : ''}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </>
                    )}

                    {showsHoldout && (
                        <div className="tw:flex tw:flex-col tw:gap-2" data-testid="warmup-holdout">
                            <p className="tw:text-sm tw:text-gray-200" data-testid="warmup-holdout-notice">
                                This licence compares warm-ups with direct starts: if Turba would start this campaign
                                with a warm-up, there is a {recommendation.holdout_share}% chance it starts directly
                                instead.
                            </p>
                            <div className="tw:flex tw:items-center tw:gap-2">
                                <Checkbox
                                    id="warmup-holdout-opt-out"
                                    checked={choice.holdoutOptOut === true}
                                    onCheckedChange={(checked: boolean | 'indeterminate') =>
                                        onChange({ ...choice, holdoutOptOut: checked === true })
                                    }
                                />
                                <Label htmlFor="warmup-holdout-opt-out" className="tw:cursor-pointer">
                                    Exclude this campaign from the comparison
                                </Label>
                            </div>
                        </div>
                    )}

                    {startChoice === START_CHOICES.AUTOMATIC && (
                        <p className="tw:text-sm tw:text-gray-200" data-testid="warmup-automatic-note">
                            Automated mode: when the campaign is published Turba reads your pixel, decides whether a
                            warm-up helps and chooses its event. It expands the campaign by itself within your maximum.
                        </p>
                    )}

                    <div className="tw:flex tw:flex-col tw:gap-1">
                        <Label htmlFor="warmup-budget">Daily budget of the warm-up ad set</Label>
                        <Input
                            id="warmup-budget"
                            inputMode="decimal"
                            value={choice.warmupBudget ?? ''}
                            onChange={(event) => onChange({ ...choice, warmupBudget: event.target.value })}
                        />
                    </div>
                </div>
            )}

            {unavailable !== 'currency_not_supported' && (
                <div className="tw:flex tw:flex-col tw:gap-1">
                    <Label htmlFor="warmup-max-budget">
                        Maximum daily budget{showsWarmupFields ? '' : ' (optional)'}
                    </Label>
                    <Input
                        id="warmup-max-budget"
                        inputMode="decimal"
                        value={choice.maxDailyBudget ?? ''}
                        onChange={(event) => onChange({ ...choice, maxDailyBudget: event.target.value })}
                    />
                    <span className="tw:text-sm tw:text-gray-200">
                        The daily budgets of all active ad sets of this campaign never add up to more than this.
                        {audienceAmounts.length > 0 &&
                            ` Your audiences use ${formatCurrencyAutoLocale({
                                amount: audienceAmounts.reduce((sum, amount) => sum + (Number(amount) || 0), 0),
                                currency,
                            })} a day${showsWarmupFields ? ', plus the warm-up ad set' : ''}.`}
                    </span>
                </div>
            )}

            {problem && (
                <p className="tw:text-sm tw:text-yellow-300" role="alert" data-testid="warmup-problem">
                    {problem}
                </p>
            )}
        </section>
    )
}

export default WarmupSection
